import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import React from 'react';
import ReactPlayer from 'react-player/youtube';
import { useVideoPlayer } from '../../context/VideoPlayerProvider';
import type ReactPlayerType from 'react-player';
import useClientSide from '../../hooks/useClientSide';
import Image from 'next/image';
import { faPlayCircle } from '@fortawesome/pro-regular-svg-icons/faPlayCircle';
import { OnProgressProps } from 'react-player/base';
import { bg5 } from '@osmosis/tailwind-ui/styles/background';
import { text0 } from '@osmosis/tailwind-ui/styles/text';

type Props = {
  url: string;
  poster?: string;
  onProgress?: (state: OnProgressProps) => void;
};

/**
 * YouTube player component.
 */
const YouTubePlayer = (props: Props) => {
  const { url, poster, onProgress } = props;

  const { setPlayer, handlePlayPause, playing, isStarted, setProgressState } =
    useVideoPlayer();

  const isClientSide = useClientSide();

  const handleOnReady = (currentPlayer: ReactPlayerType): void => {
    setPlayer(currentPlayer);
  };

  const handleProgress = (state: OnProgressProps) => {
    setProgressState(state);

    onProgress?.(state);
  };

  return (
    <Box>
      <Box cn="w-full relative pb-[56%]">
        {/* Loading state */}
        {!isStarted && (
          <Box
            cn={`absolute w-full h-full ${bg5}`}
            data-testid="youtube-player-play-pause"
            onClick={handlePlayPause}
          >
            {poster && (
              <>
                <Box cn="absolute z-[1] bg-black/75 w-full h-full" />
                <Image
                  src={poster}
                  fill={true}
                  alt="video-poster"
                  loading="eager"
                  fetchPriority="high"
                  className="relative z-[0]"
                  data-testid="youtube-player-poster"
                />
              </>
            )}

            <Box
              cn={`absolute z-[1] text-16 ${text0} w-full h-full flex justify-center items-center`}
            >
              <Icon icon={faPlayCircle} />
            </Box>
          </Box>
        )}

        {/* Show video only on client */}
        <Box cn={`absolute w-full h-full ${isStarted ? '' : 'hidden'}`}>
          {isClientSide && (
            <ReactPlayer
              width="100%"
              height="100%"
              url={url}
              controls={true}
              playing={playing}
              onReady={handleOnReady}
              onProgress={handleProgress}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default YouTubePlayer;
